import { Injectable, computed, inject, signal } from '@angular/core';
import { EMPTY, Observable, ReplaySubject, concat, of, tap } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { MeApiService } from '~shared/services/api/data/me.api-service';
import { readStoredToken } from '~shared/services/auth/auth-storage.util';
import {
  MeGroup,
  MeResponse,
  MePermissions,
  MeSystemTable,
  MeUser,
} from '~shared/models/me-response.model';

const ME_CACHE_KEY = 'me_cache_v1';
const ME_CACHE_TTL_MS = 30 * 60 * 1000;

type MeCacheEntry = {
  token: string;
  ts: number;
  data: MeResponse;
};

@Injectable({ providedIn: 'root' })
export class CurrentUserService {
  private readonly meApiService = inject(MeApiService);
  private readonly state = signal<MeResponse | null>(null);
  private readonly loaded$ = new ReplaySubject<MeResponse>(1);

  setMe(me: MeResponse): void {
    this.state.set(me);
    this.loaded$.next(me);
  }

  clear(): void {
    this.state.set(null);
    this.clearCache();
  }

  /**
   * Fetches /me and stores the result. Called post-login and at bootstrap
   * when a JWT is already present in localStorage. Uses a localStorage
   * cache bound to the current JWT (TTL 5 min) to avoid 269+ Doctrine
   * queries on every hard navigation.
   */
  load(): Observable<MeResponse> {
    const token = this.readToken();
    const cached = token ? this.readCache(token) : null;
    const network$ = this.meApiService.getMe().pipe(
      tap((me) => {
        this.setMe(me);
        if (token) {
          this.writeCache(token, me);
        }
      })
    );
    if (cached) {
      this.setMe(cached);
      // Stale-while-revalidate : on peint immédiatement depuis le cache (rapide,
      // évite les ~269 requêtes Doctrine de /me sur chaque hard navigation),
      // puis on revalide en tâche de fond. Les souscripteurs (ex: bootstrap qui
      // pousse les modules dans le menu) reçoivent d'abord le cache, puis la
      // version fraîche — ce qui reflète un droit modifié dès le refresh, sans
      // attendre le TTL ni une déconnexion. Une revalidation en échec ne casse
      // rien : on conserve la valeur cache.
      return concat(of(cached), network$.pipe(catchError(() => EMPTY)));
    }
    return network$;
  }

  /**
   * Forces a fresh /me fetch, bypassing and refreshing the localStorage cache.
   * Call this after the current user's own rights (or those of a group they
   * belong to) changed, so the menu and gating reflect the new permissions
   * without waiting for the 30 min cache TTL.
   */
  reload(): Observable<MeResponse> {
    this.clearCache();
    return this.meApiService.getMe().pipe(
      tap((me) => {
        this.setMe(me);
        const token = this.readToken();
        if (token) {
          this.writeCache(token, me);
        }
      })
    );
  }

  private readToken(): string | null {
    return readStoredToken();
  }

  private readCache(token: string): MeResponse | null {
    try {
      const raw = localStorage.getItem(ME_CACHE_KEY);
      if (!raw) return null;
      const entry = JSON.parse(raw) as MeCacheEntry;
      if (entry.token !== token) return null;
      if (Date.now() - entry.ts > ME_CACHE_TTL_MS) return null;
      return entry.data;
    } catch {
      return null;
    }
  }

  private writeCache(token: string, data: MeResponse): void {
    try {
      const entry: MeCacheEntry = { token, ts: Date.now(), data };
      localStorage.setItem(ME_CACHE_KEY, JSON.stringify(entry));
    } catch {
      // Quota exceeded or serialization issue — degrade silently.
    }
  }

  private clearCache(): void {
    try {
      localStorage.removeItem(ME_CACHE_KEY);
    } catch {
      // Storage unavailable — nothing to clear.
    }
  }

  /**
   * Emits the MeResponse once loaded. If already loaded, emits immediately.
   * Guards use this to wait for /me before making access decisions.
   */
  waitForMe(): Observable<MeResponse | null> {
    const current = this.state();
    if (current !== null) {
      return of(current);
    }
    return this.loaded$.pipe(take(1));
  }

  user = computed<MeUser | null>(() => this.state()?.user ?? null);

  permissions = computed<MePermissions | null>(
    () => this.state()?.permissions ?? null
  );

  systemTables = computed<MeSystemTable[]>(
    () => this.state()?.permissions.system ?? []
  );

  groups = computed<MeGroup[]>(() => this.state()?.groups ?? []);

  /** True if the current user is a member of the given group. */
  isMemberOfGroup(groupId: number): boolean {
    return this.groups().some((g) => g.id === groupId);
  }

  isAdmin(): Observable<boolean> {
    return this.waitForMe().pipe(map((me) => me?.user.is_admin === true));
  }

  /**
   * True if the user has any CRUD permission on at least one system table.
   * Used to decide whether to show the sidebar "Admin" menu.
   */
  hasAnySystemPermission(): boolean {
    const system = this.state()?.permissions.system ?? [];
    return system.some((t) => t.read || t.create || t.update || t.delete);
  }
}
